import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Link } from "react-router-dom";
import SafeRichText from '../components/SafeRichText';
import LoadingSpinner from '../components/LoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/backend/php';

// Resolve team member photo URL exactly like AdvisorsPage: always request backend API endpoint
function resolvePhotoUrl(member, apiBase, cacheBustTimestamp) {
  if (!member || !member.id) return null;
  const base = apiBase.replace(/\/+$/, '');
  return `${base}/api/team/${member.id}/photo?t=${cacheBustTimestamp}`;
}

const getInitials = (name) => {
  if (!name) return '';
  return name
    .split(" ")
    .map(word => word[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();
};

export default function OurTeam() {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imgErrors, setImgErrors] = useState({});
  const cacheBustTimestamp = React.useRef(Date.now());
  
  useEffect(() => {
    setLoading(true);
    axios.get(`${API_BASE_URL}/api/team`, {
      params: { cb: Date.now() }
    })
      .then(res => {
        setTeam(res.data || []);
        setError(null);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Team API Error:", error);
        setError('Failed to load team data');
        setTeam([]);
        setLoading(false);
      });
  }, []);
  
  // Ensure team is always an array to prevent null errors
  const teamData = team || [];
  
  // Sort team data by ID (ascending order)
  const sortedTeamData = React.useMemo(() => {
    return [...teamData].sort((a, b) => (a.id || 0) - (b.id || 0));
  }, [teamData]);

  const cardVariants = {
    hidden: { opacity: 0, y: 60, scale: 0.8, rotateX: -15 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      scale: 1,
      rotateX: 0,
      transition: { delay: i * 0.12, type: 'spring', stiffness: 120, damping: 15, duration: 0.6 }
    }),
    hover: { y: -20, scale: 1.05, transition: { type: 'spring', stiffness: 400, damping: 25 } }
  };

  return (
    <div className="w-full max-w-8xl rounded-lg sm:rounded-xl md:rounded-2xl shadow-xl p-2 sm:p-4 md:p-8 text-sm sm:text-base overflow-x-auto" style={{ background: 'rgba(245, 247, 255, 0.85)', backdropFilter: 'blur(16px)', WebkitBackdropFilter: 'blur(16px)' }}>
      <div className="min-h-screen relative overflow-hidden">
        {/* Gradient background (same as OurStory) */}
        <div className="fixed inset-0 bg-slate-50" />

        <div className="relative z-10 px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
          <div className="max-w-7xl mx-auto">
            <motion.div className="text-center mb-12" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 text-black">Founders</h1>
              <p className="text-lg text-black max-w-2xl mx-auto">Meet the people behind VenturePoint for Economic Development — the experts and practitioners who drive our work.</p>
            </motion.div>

            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }}>
              {loading && (
                <div className="flex justify-center py-20">
                  <LoadingSpinner message="Loading team..." bgColor="bg-transparent" spinnerColor="border-[#73ab40]" py={0} />
                </div>
              )}

              {error && (
                <div className="text-center py-16">
                  <div className="bg-blue-50 text-blue-600 p-6 rounded-lg inline-block">Founders will be available soon. Check back later!</div>
                </div>
              )}

              {/* First team member centered on its own row */}
              {sortedTeamData.length > 0 && (
                <div className="mb-8 flex justify-center">
                  {(() => {
                    const member = sortedTeamData[0];
                    const idx = 0;
                    return (
                      <Link key={member.id || 'first-member'} to={`/team/${member.id}`} className="block w-full sm:w-3/4 md:w-2/3 lg:w-1/2 group">
                        <motion.div className="relative" custom={idx} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants} whileHover="hover">
                          <div className="relative h-[420px] p-6 bg-white/10 backdrop-blur-xl rounded-3xl border border-emerald/20 hover:border-emerald/40 shadow-lg cursor-pointer overflow-hidden flex flex-col items-center text-center justify-between">
                            <div className="mb-6 relative">
                              <div className="relative">
                                {(() => { 
                                  const imgUrl = resolvePhotoUrl(member, API_BASE_URL, cacheBustTimestamp.current);
                                  
                                  // Show initials fallback if error occurred
                                  if (imgErrors[member.id]) {
                                    return (
                                      <div className="w-32 h-32 bg-gradient-to-br from-yellow-400 via-gold to-yellow-500 rounded-full flex items-center justify-center shadow-2xl relative overflow-hidden">
                                        <span className="text-2xl font-bold text-slate-900 select-none relative z-10">
                                          {getInitials(member.name)}
                                        </span>
                                      </div>
                                    );
                                  }
                                  
                                  return (
                                    <img 
                                      src={imgUrl} 
                                      className="w-32 h-32 object-cover rounded-full shadow-2xl border-3 border-emerald/40" 
                                      alt={member.name} 
                                      onError={() => {
                                        console.error('Team image failed:', { id: member.id, url: imgUrl });
                                        setImgErrors(prev => ({ ...prev, [member.id]: true }));
                                      }} 
                                    />
                                  );
                                })()}
                              </div>
                            </div>

                            <h3 className="text-xl md:text-2xl font-bold mb-2 text-black">{member.name}</h3>
                            <div className="text-emerald-400 font-semibold text-sm mb-4">{member.role}</div>

                            <div className="text-black text-sm leading-relaxed flex-grow overflow-hidden max-h-[5rem]">
                              <SafeRichText content={member.bio ? member.bio.split(' ').slice(0, 12).join(' ') + (member.bio.split(' ').length > 12 ? '...' : '') : ''} />
                            </div>
                          </div>
                        </motion.div>
                      </Link>
                    );
                  })()} 
                </div>
              )}

              {/* Remaining members in grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
                {sortedTeamData.slice(1).map((member, idx) => {
                  const realIdx = idx + 1;
                  return (
                    <Link key={member.id || realIdx} to={`/team/${member.id}`} className="block h-full group">
                      <motion.div className="relative h-full" custom={realIdx} initial="hidden" whileInView="visible" viewport={{ once: true }} variants={cardVariants} whileHover="hover">
                        <div className="relative h-[420px] p-6 bg-white/10 backdrop-blur-xl rounded-3xl border border-emerald/20 hover:border-emerald/40 shadow-lg cursor-pointer overflow-hidden flex flex-col items-center text-center justify-between">
                          <div className="mb-6 relative">
                            <div className="relative">
                              {(() => { 
                                const imgUrl = resolvePhotoUrl(member, API_BASE_URL, cacheBustTimestamp.current);
                                if (imgErrors[member.id]) {
                                  return (
                                    <div className="w-32 h-32 bg-gradient-to-br from-yellow-400 via-gold to-yellow-500 rounded-full flex items-center justify-center shadow-2xl relative overflow-hidden">
                                      <span className="text-2xl font-bold text-slate-900 select-none relative z-10">
                                        {getInitials(member.name)}
                                      </span>
                                    </div>
                                  );
                                }
                                return (
                                  <img 
                                    src={imgUrl} 
                                    className="w-32 h-32 object-cover rounded-full shadow-2xl border-3 border-emerald/40" 
                                    alt={member.name} 
                                    onError={() => setImgErrors(prev => ({ ...prev, [member.id]: true }))} 
                                  />
                                );
                              })()}
                            </div>
                          </div>

                          <h3 className="text-xl md:text-2xl font-bold mb-2 text-black">{member.name}</h3>
                          <div className="text-emerald-400 font-semibold text-sm mb-4">{member.role}</div>

                          <div className="text-black text-sm leading-relaxed flex-grow overflow-hidden max-h-[5rem]">
                            <SafeRichText content={member.bio ? member.bio.split(' ').slice(0, 12).join(' ') + (member.bio.split(' ').length > 12 ? '...' : '') : ''} />
                          </div>
                        </div>
                      </motion.div>
                    </Link>
                  );
                })}
              </div>

              {!loading && !error && sortedTeamData.length === 0 && (
                <div className="text-center py-16">
                  <div className="bg-white/5 rounded-2xl p-8 inline-block">No team members yet.</div>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
}